import * as React from "react";
import { proveNever } from "@glideapps/ts-necessities";
import { RouteComponentProps } from "react-router-dom";
import classNames from "classnames";

import { MembersScreenStyle } from "./members-screen-style";
import NavBreadcrumb from "../breadcrumb/nav-breadcrumb";
import { RootState, OrganizationsState } from "../../../../store/types";
import { connect } from "react-redux";
import {
    makeNavBreadcrumbItems,
    OrgMember,
    getOrgMembers,
    addOrgMember,
    removeOrgMember,
    isUserOrgID,
    getOrganizationPersistentInviteLink,
    setOrgAdmin,
} from "../orgs-lib";
import { LoadingIndicator, Card, CardSecondary, Grid } from "../orgs-styles";
import SectionHeader from "../section-header/section-header";
import { Button } from "../../../ui/smart/button/button-style";
import AppIcon from "common/app-icon";
import { removeArrayItem, isValidEmailAddress } from "common/Support";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { UserDataAndState, isUserData } from "common/Database";
import { trackEvent } from "../../../../analytics";
import copyToClipboard from "copy-to-clipboard";
import { withModalProvider } from "designer/modals-v2/modal-provider-v2/modal-provider-v2";
import { SimpleModalContextProps } from "designer/modals-v2/modal-provider-v2/modal-context";

interface RouteProps {
    readonly orgID: string | undefined;
}

interface Props extends RouteComponentProps<RouteProps> {
    readonly organizations: OrganizationsState;
    readonly userData: UserDataAndState;
    readonly modals: SimpleModalContextProps;
}

interface State {
    readonly orgMembers: readonly OrgMember[] | undefined | "loading";
    readonly showEntry: boolean;
    readonly entryValue: string;
    readonly removedMembers: readonly OrgMember[];
}

class MembersScreenImpl extends React.PureComponent<Props, State> {
    public state: State = {
        orgMembers: "loading",
        showEntry: true,
        entryValue: "",
        removedMembers: [],
    };

    private orgID(): string | undefined {
        return this.props.match.params.orgID;
    }

    private updateOrgMembers = async () => {
        const [orgMembers] = await Promise.all([getOrgMembers(this.orgID())]);
        this.setState({ orgMembers });
    };

    private pollHandle: number | undefined;
    public async componentDidMount() {
        await this.updateOrgMembers();

        this.pollHandle = window.setInterval(async () => {
            if (this.state.orgMembers === "loading" || this.state.orgMembers === undefined) return;
            if (this.state.orgMembers.some(m => m.status === "pending")) {
                await this.updateOrgMembers();
            }
        }, 10000);
    }

    public componentWillUnmount() {
        window.clearInterval(this.pollHandle);
    }

    private showEntry = () => this.setState({ showEntry: true });
    // private hideEntry = () => this.setState({ showEntry: false });

    private onAddMemberEntryKeypress = async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            await this.onAddClick();
        }
    };

    private makeNewInviteLink = async () => {
        const orgID = this.orgID();
        if (orgID === undefined) return;

        const persistentInviteLink = await getOrganizationPersistentInviteLink(orgID, true);

        return persistentInviteLink;
    };

    private onAddClick = async () => {
        // this.hideEntry();
        const orgID = this.orgID();
        if (orgID === undefined || !isValidEmailAddress(this.state.entryValue)) return;

        const email = this.state.entryValue;
        if (this.state.orgMembers === "loading" || this.state.orgMembers === undefined) return;

        if (this.state.orgMembers.find(o => o.email.toLowerCase() === email)) {
            await this.props.modals.showSimpleInfoModal(
                "Cannot invite",
                `User with email ${email} already exists in this team.`
            );
            return;
        }

        this.setState({
            entryValue: "",
            orgMembers: [
                ...this.state.orgMembers,
                {
                    userID: "-1",
                    displayName: "Adding…",
                    email,
                    photoURL: undefined,
                    role: "editor",
                    status: "pending",
                },
            ],
        });

        const newList = await addOrgMember(orgID, email);
        this.setState({ orgMembers: newList });

        trackEvent("org_member_invited", { org_id: orgID, source: "members screen" });
    };

    private onEntryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ entryValue: event.target.value });
    };

    private onButtonMouseDown = (event: React.MouseEvent) => {
        event.preventDefault();
    };

    private getRole(member: OrgMember): React.ReactNode {
        if (member.role === "admin") {
            return <span className="mems-admin">Owner</span>;
        }
        return "Member";
    }

    private getStatus(member: OrgMember): string {
        if (member.status === "pending") {
            return "Pending";
        } else if (member.status === "active") {
            return "Active";
        }
        return proveNever(member.status, "", "Active");
    }

    private onRemoveMember = async (member: OrgMember) => {
        const r = await this.props.modals.showAcceptCancelModal(
            "Remove member",
            `Are you sure you want to remove ${member.displayName}?`,
            "Remove",
            "danger"
        );

        if (!r) return;
        this.setState({ removedMembers: [member, ...this.state.removedMembers] });

        const result = await removeOrgMember(this.orgID(), member);

        if (!result) {
            this.setState({
                removedMembers: removeArrayItem(this.state.removedMembers, this.state.removedMembers.indexOf(member)),
            });
        }
    };

    private onMakeAdmin = async (member: OrgMember) => {
        const r = await this.props.modals.showAcceptCancelModal(
            "Make member admin",
            `Are you sure you want to make ${member.displayName} the admin of your team? You will no longer be the team admin.`,
            "Confirm",
            "default"
        );

        if (!r) return;

        if (await setOrgAdmin(this.orgID(), member)) {
            await this.updateOrgMembers();
        }
    };

    private copyTeamInviteLink = async () => {
        const link = await this.makeNewInviteLink();
        if (link !== undefined) {
            copyToClipboard(link);
        }
    };

    public render(): React.ReactNode {
        const { organizations, match, userData } = this.props;
        const { orgMembers } = this.state;
        const orgID = match.params.orgID;

        if (!isUserData(userData)) return null;

        let content: React.ReactNode;

        if (orgMembers === "loading") {
            content = <LoadingIndicator />;
        } else if (orgMembers === undefined) {
            content = <SectionHeader name="Could not fetch members" />;
        } else {
            const isAdmin = orgMembers.find(mem => mem.userID === userData.id)?.role === "admin";
            const addSquare = "01-43-add-square";
            content = (
                <>
                    <TransitionGroup>
                        <CSSTransition key={"fudge"} appear={true} classNames="mems-trans" timeout={200}>
                            <Grid>
                                <main>
                                    <Card>
                                        <div className="card-header">
                                            {this.state.showEntry && (
                                                <div className="mems-entry-wrap">
                                                    <input
                                                        value={this.state.entryValue}
                                                        onChange={this.onEntryChange}
                                                        autoFocus={true}
                                                        // onBlur={this.hideEntry}
                                                        onKeyPress={this.onAddMemberEntryKeypress}
                                                        placeholder="Enter email…"
                                                    />
                                                    <button
                                                        disabled={!isValidEmailAddress(this.state.entryValue)}
                                                        onMouseDown={this.onButtonMouseDown}
                                                        onClick={this.onAddClick}>
                                                        Send Invite
                                                    </button>
                                                </div>
                                            )}
                                            {!this.state.showEntry && (
                                                <Button onClick={this.showEntry} variant="inline">
                                                    Add members
                                                    <AppIcon className="mems-button-icon" icon={addSquare} size={20} />
                                                </Button>
                                            )}
                                        </div>
                                        <div
                                            className={classNames(
                                                "mems-grid-row",
                                                "mems-grid-header",
                                                isAdmin && "admin"
                                            )}>
                                            <div>
                                                {orgMembers.length} member{orgMembers.length > 1 ? "s" : ""}
                                            </div>
                                            <div>Email</div>
                                            <div>Role</div>
                                            <div>Status</div>
                                        </div>
                                        {orgMembers
                                            .filter(m => this.state.removedMembers.indexOf(m) === -1)
                                            .map(m => {
                                                const isSelf = m.userID === userData.id;

                                                return (
                                                    <div
                                                        key={m.email}
                                                        className={classNames("mems-grid-row", isAdmin && "admin")}>
                                                        <div className="mems-user-wrapper">
                                                            {m.photoURL === undefined ? (
                                                                <div className="mems-user-photo" />
                                                            ) : (
                                                                <img
                                                                    className="mems-user-photo"
                                                                    src={m.photoURL}
                                                                    alt=""
                                                                />
                                                            )}
                                                            <div>{m.displayName}</div>
                                                        </div>
                                                        <div>{m.email}</div>
                                                        <div>{this.getRole(m)}</div>
                                                        <div>{this.getStatus(m)}</div>
                                                        {isAdmin &&
                                                            (!isSelf && m.role !== "admin" && m.status === "active" ? (
                                                                <button
                                                                    className={classNames("mems-admin-button")}
                                                                    onClick={() => this.onMakeAdmin(m)}>
                                                                    Make Admin
                                                                </button>
                                                            ) : (
                                                                <div />
                                                            ))}
                                                        {(isSelf || isAdmin) && m.role !== "admin" && (
                                                            <button
                                                                className={classNames(
                                                                    "mems-remove-button",
                                                                    isAdmin && "btn-ghost"
                                                                )}
                                                                onClick={() => this.onRemoveMember(m)}>
                                                                Remove
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </Card>
                                </main>
                                <aside>
                                    <CardSecondary>
                                        <div className="card-content">
                                            <h2 className="card-title">Invite your team with a link.</h2>
                                            <p>
                                                They will be able to join your team directly once they signup to Glide.
                                            </p>
                                            {!isUserOrgID(orgID) && (
                                                <Button onClick={this.copyTeamInviteLink} variant="filled">
                                                    Copy Invite Link
                                                </Button>
                                            )}
                                        </div>
                                    </CardSecondary>
                                    <div className="mems-warning">
                                        <AppIcon className="warn" icon="01-14-information-circle" size={16} />
                                        <p>
                                            Members can create, edit, and upgrade apps. They can also access app data
                                            and invite other members.
                                        </p>
                                    </div>
                                </aside>
                            </Grid>
                        </CSSTransition>
                    </TransitionGroup>
                </>
            );
        }

        return (
            <MembersScreenStyle>
                <div className="mems-nav">
                    <NavBreadcrumb items={makeNavBreadcrumbItems("Members", orgID, organizations)} />
                </div>
                {content}
            </MembersScreenStyle>
        );
    }
}

const mapState = (state: RootState) => ({
    organizations: state.organizations,
    userData: state.userData,
});

const MembersScreen = withModalProvider(connect(mapState)(MembersScreenImpl));
export default MembersScreen;
